import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { watchlistActions } from './watchlist.actions';
import { WatchlistVM } from './watchlist.entity';
import { watchlistFeature } from './watchlist.reducers';
import {
  selectIsOnWatchlist,
  selectWatchlistCryptoItems,
  selectWatchlistHasNotes,
  selectWatchlistItem,
  selectWatchlistItems,
} from './watchlist.selectors';

@Injectable({ providedIn: 'root' })
export class WatchlistFacade {
  private readonly store = inject(Store);

  public readonly watchlist$ = this.store.select(watchlistFeature.selectAll);
  public readonly watchlistItems$ = this.store.select(selectWatchlistItems);
  public readonly watchlistCryptoItems$ = this.store.select(selectWatchlistCryptoItems);
  public readonly watchlistLoading$ = this.store.select(watchlistFeature.selectLoading);
  public readonly watchlistInitialLoaded$ = this.store.select(watchlistFeature.selectInitialLoaded);
  public readonly hasNotes$ = this.store.select(selectWatchlistHasNotes);
  public readonly error$ = this.store.select(watchlistFeature.selectError);
  public readonly selectWatchlistCategories$ = this.store.select(watchlistFeature.selectCategories);

  public loadWatchlist(force = false): void {
    this.store.dispatch(watchlistActions.loadWatchlist({ force }));
  }

  public isOnWatchlist$(isin: string): Observable<boolean> {
    return this.store.select(selectIsOnWatchlist(isin));
  }

  public getWatchlistItemByIsin$(isin: string): Observable<WatchlistVM> {
    return this.store.select(selectWatchlistItem(isin));
  }

  public addWatchlistItem(isin: string): void {
    return this.store.dispatch(watchlistActions.addWatchlistItem({ isin }));
  }

  public removeWatchlistItem(isin: string): void {
    return this.store.dispatch(watchlistActions.removeWatchlistItem({ isin }));
  }

  public saveWatchlist(): void {
    return this.store.dispatch(watchlistActions.saveWatchlist());
  }

  public reorderWatchlistItem(from: number, to: number): void {
    return this.store.dispatch(watchlistActions.reorderWatchlistItem({ from, to }));
  }

  public updateWatchlistItemNote(isin: string, note: string): void {
    return this.store.dispatch(watchlistActions.updateWatchlistItemNote({ isin, note }));
  }

  public updateWatchlistItemEntryQuote(isin: string, entryQuote: number, basedOnEntry = true): void {
    return this.store.dispatch(watchlistActions.updateWatchlistItemEntryQuote({ isin, entryQuote, basedOnEntry }));
  }

  public subscribeToWatchlist(basedOnEntry = true): void {
    this.store.dispatch(watchlistActions.subscribeToWatchlistQuotes({ basedOnEntry }));
  }

  public unsubscribeFromWatchlist(): void {
    this.store.dispatch(watchlistActions.unsubscribeFromWatchlistQuotes());
  }

  public subscribeToWatchlistItem(isin: string, basedOnEntry = true): void {
    this.store.dispatch(watchlistActions.subscribeToWatchlistItemQuote({ isin, basedOnEntry }));
  }

  public unsubscribeFromWatchlistItem(isin: string): void {
    this.store.dispatch(watchlistActions.unsubscribeFromWatchlistItemQuote({ isin }));
  }
}
